import { useRef, useState } from 'react';
import { formatDate } from '../../../lib/format';
import type { EmployeeDocumentRow, DocumentCategory } from '@dru/types';
import {
  DOCUMENT_CATEGORY_LABEL, DOCUMENT_CATEGORY_HINT, formatBytes,
} from '@dru/types';
import {
  Alert, Button, Card, CardHeader, DataTable, Dialog, IconAction, InfoTip, RowActions, Select, SelectField, StatusBadge, TextField,
} from '@dru/ui';
import { Eye, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { useAuth } from '../../../lib/auth';
import { toBase64 } from '../../../lib/files';

/**
 * Files attached to a worker's record.
 *
 * This replaces the legacy platform's casualty module, which turned out to be
 * four fields and a file list. Casualty is a category here rather than a
 * screen of its own, which also gives paper consent scans and externally
 * issued certificates somewhere to live.
 *
 * Two things this deliberately does not do.
 *
 * It holds no clinical content. A document says a file exists and what it is
 * about; anything clinical belongs in the examination record, where it is
 * typed, validated and released under a signature.
 *
 * And it never links to a file directly. Every open mints a five-minute grant
 * and is audited server-side — the question after an incident is who opened a
 * worker's paperwork, and a durable URL cannot answer it.
 */

const CATEGORIES: DocumentCategory[] = [
  'CASUALTY', 'EXTERNAL_MEDICAL', 'EXTERNAL_FITNESS', 'CONSENT_SCAN', 'IDENTITY', 'OTHER',
];

/** Matches the server allowlist. An allowlist, because a blocklist is a list
 *  of the attacks somebody thought of. */
const ACCEPT = 'application/pdf,image/jpeg,image/png';

/**
 * The adapter's body limit caps a base64 payload at ~1.4 MB, so a larger file
 * is refused here with a sentence rather than by the connection dropping.
 * Moving to multipart or presigned upload is tracked as technical debt in
 * docs/25 — this number should disappear when that lands.
 */
const MAX_BYTES = 1_000_000;

const day = (d?: string | null) => (d ? formatDate(d) : '—');

export function DocumentsTab({ employeeId }: { employeeId: string }) {
  const { permissions } = useAuth();
  const [category, setCategory] = useState<DocumentCategory | ''>('');
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [removing, setRemoving] = useState<EmployeeDocumentRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const docs = useQuery<EmployeeDocumentRow[]>(
    () => api.get<EmployeeDocumentRow[]>(
      `/employees/${employeeId}/documents${category ? `?category=${category}` : ''}`),
    [employeeId, category],
  );

  const canUpload = permissions.has('employeedoc.upload');
  const canDelete = permissions.has('employeedoc.delete');

  const open = async (id: string) => {
    setOpening(id);
    setError(null);
    try {
      const grant = await api.get<{ url: string }>(
        `/employees/${employeeId}/documents/${id}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setOpening(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Documents"
        hint="Scans and paperwork on this employee's record. Opening one is recorded."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory | '')}
              className="w-auto"
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{DOCUMENT_CATEGORY_LABEL[c]}</option>
              ))}
            </Select>
            {canUpload && <Button onClick={() => setUploading(true)}>Attach a file</Button>}
          </div>
        }
      />

      {error && <div className="px-4 pt-3"><Alert tone="danger">{error}</Alert></div>}

      <DataTable<EmployeeDocumentRow>
        rows={docs.data ?? []}
        loading={docs.loading}
        error={docs.error}
        onRetry={docs.reload}
        rowKey={(d) => d.id}
        empty={{
          title: category ? 'Nothing in this category' : 'No documents attached',
          hint: canUpload
            ? 'Attach a scan of an incident report, a signed consent, or a certificate issued elsewhere.'
            : 'Documents attached by HR appear here.',
        }}
        columns={[
          {
            key: 'title', header: 'Document', alwaysVisible: true,
            cell: (d) => (
              <div>
                <p className="font-medium text-neutral-900">{d.title}</p>
                <p className="text-caption text-neutral-500">
                  {d.fileName} · {formatBytes(d.sizeBytes)}
                </p>
              </div>
            ),
          },
          {
            key: 'category', header: 'Category',
            cell: (d) => (
              <StatusBadge tone={d.category === 'CASUALTY' ? 'warning' : 'neutral'}>
                {DOCUMENT_CATEGORY_LABEL[d.category] ?? d.category}
              </StatusBadge>
            ),
          },
          {
            key: 'occurred', header: 'Date of event',
            // The injury date, not the upload date. They are usually different
            // and the first is the one anybody searches by.
            cell: (d) => day(d.occurredOn),
          },
          {
            key: 'added', header: 'Attached', hideOnMobile: true,
            cell: (d) => day(d.createdAt),
          },
          {
            key: 'notes', header: 'Notes', defaultHidden: true,
            cell: (d) => d.notes ?? '—',
          },
          {
            key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (d) => (
              <RowActions>
                <IconAction
                  label={opening === d.id ? `Opening ${d.title}` : `Open ${d.title}`}
                  icon={Eye}
                  onClick={() => open(d.id)}
                  loading={opening === d.id}
                />
                {canDelete && (
                  <IconAction label={`Remove ${d.title}`} icon={Trash2} danger onClick={() => setRemoving(d)} />
                )}
              </RowActions>
            ),
          },
        ]}
      />

      <p className="flex items-center gap-1 border-t border-neutral-200 px-4 py-3 text-caption text-neutral-500">
        Every time a document here is opened, it is written to the audit trail.
        <InfoTip label="About document storage">
          The audit trail records who opened it and when. Files are held in private storage and the
          link created for an open expires in five minutes.
        </InfoTip>
      </p>

      {uploading && (
        <UploadDialog
          employeeId={employeeId}
          onClose={() => setUploading(false)}
          onDone={() => { setUploading(false); docs.reload(); }}
        />
      )}
      {removing && (
        <RemoveDialog
          employeeId={employeeId}
          doc={removing}
          onClose={() => setRemoving(null)}
          onDone={() => { setRemoving(null); docs.reload(); }}
        />
      )}
    </Card>
  );
}

function UploadDialog(
  { employeeId, onClose, onDone }:
  { employeeId: string; onClose: () => void; onDone: () => void },
) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<DocumentCategory>('CASUALTY');
  const [title, setTitle] = useState('');
  const [occurredOn, setOccurredOn] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooBig = !!file && file.size > MAX_BYTES;
  const valid = !!file && !tooBig && title.trim().length >= 2;

  const pick = (f: File | null) => {
    setFile(f);
    setError(null);
    // The filename is almost always the right caption, and nobody enjoys
    // typing one. Still editable — "IMG_4021.jpg" is not a caption.
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 160));
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const content = await toBase64(file);
      await api.post(`/employees/${employeeId}/documents`, {
        category,
        title: title.trim(),
        occurredOn: occurredOn || undefined,
        notes: notes.trim() || undefined,
        fileName: file.name,
        mimeType: file.type,
        content,
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} title="Attach a document">
      <div className="space-y-3">
        <SelectField
          label="Category" required value={category}
          onChange={(v) => setCategory(v as DocumentCategory)}
          hint={DOCUMENT_CATEGORY_HINT[category]}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{DOCUMENT_CATEGORY_LABEL[c]}</option>
          ))}
        </SelectField>

        <div>
          <span className="flex items-center gap-1">
            <label htmlFor="document-file" className="text-caption text-neutral-700">File<span className="ml-0.5 text-danger-600" aria-hidden>*</span></label>
            <InfoTip label="About the file">PDF, JPEG or PNG, up to {formatBytes(MAX_BYTES)}.</InfoTip>
          </span>
          <input
            id="document-file"
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full text-small text-neutral-700
                       file:mr-3 file:rounded-btn file:border-0 file:bg-primary-600
                       file:px-3 file:py-2 file:text-sm file:font-medium file:text-white"
          />
        </div>

        {tooBig && (
          <Alert tone="warning">
            That file is {formatBytes(file!.size)}, over the {formatBytes(MAX_BYTES)} limit.
            Larger uploads are a known gap — for now, a lower-resolution scan or a split PDF
            will go through.
          </Alert>
        )}

        <TextField
          label="Title" required value={title} onChange={setTitle}
          placeholder="Incident report, 12 March"
          info="The only thing telling the next reader what this is."
        />
        <TextField
          label="Date of the event (optional)" type="date" value={occurredOn} onChange={setOccurredOn}
          hint="When the incident happened — not today's date."
        />
        <TextField
          label="Notes (optional)" value={notes} onChange={setNotes}
          hint="Context, not clinical findings — those belong in the medical case."
        />

        {error && <Alert tone="danger">{error}</Alert>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy}>
            {busy ? 'Uploading…' : 'Attach'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function RemoveDialog(
  { employeeId, doc, onClose, onDone }:
  { employeeId: string; doc: EmployeeDocumentRow; onClose: () => void; onDone: () => void },
) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/employees/${employeeId}/documents/${doc.id}`, { reason });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} title={`Remove “${doc.title}”?`}>
      <div className="space-y-3">
        <Alert tone="info">
          The document stops appearing on this employee's record. It is not destroyed — a retention
          period this platform does not control may still be running on it, and removing something
          from someone's record is itself worth remembering.
        </Alert>

        <TextField
          label="Why is it being removed?" required value={reason} onChange={setReason}
          placeholder="Attached to the wrong employee" autoFocus
          hint="At least five characters."
          info="Kept with the record of the removal."
        />

        {error && <Alert tone="danger">{error}</Alert>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={reason.trim().length < 5 || busy}>
            {busy ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
